import { H2, P, Ul, Code, CodeBlock, Callout } from '../../components/docs/primitives';
import { INDEX_REPO, PUBLISHING_URL, SPEC } from './links';

const SIGN_COMMANDS = `minisign -Sm my-plugin-1.0.0.zip
certutil -hashfile my-plugin-1.0.0.zip SHA256`;

const INDEX_ENTRY = `{
  "id": "yourhandle.my-plugin",
  "name": "My Plugin",
  "version": "1.0.0",
  "tier": "A",
  "description": "One or two plain sentences about what it does.",
  "homepage": "https://github.com/yourhandle/my-plugin",
  "host_min": "8.0.0",
  "released_at": "2026-06-10T00:00:00Z",
  "author": {
    "name": "yourhandle",
    "pubkey": "RW...your minisign public key..."
  },
  "artifact": {
    "url": "https://github.com/yourhandle/my-plugin/releases/download/v1.0.0/my-plugin-1.0.0.zip",
    "sha256": "<hex of the zip>",
    "signature_url": "https://github.com/yourhandle/my-plugin/releases/download/v1.0.0/my-plugin-1.0.0.zip.minisig"
  }
}`;

export default function Publishing() {
  return (
    <>
      <H2 id="keys">Create your signing key</H2>
      <P>
        All signing uses{' '}
        <a
          href="https://jedisct1.github.io/minisign/"
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline"
        >
          minisign
        </a>
        , so stock tooling works everywhere:
      </P>
      <CodeBlock code="minisign -G" />
      <P>
        Keep the secret key offline. Your public key is your identity to every user: the first
        install pins it, and every later install and update must verify against it. A key change
        without a rotation proof (a second signature by the old key) blocks updates for everyone
        who has your plugin installed.
      </P>

      <H2 id="package">Package and sign</H2>
      <P>
        Zip the plugin folder contents so <Code>plugin.toml</Code> sits at the zip root, not
        inside a subfolder. Then sign the zip and compute the hash your index entry will carry:
      </P>
      <CodeBlock code={SIGN_COMMANDS} />

      <H2 id="host">Host the release</H2>
      <P>
        Upload the zip and the <Code>.minisig</Code> to a stable direct-download location you
        control. A GitHub release on your plugin's repository is the expected shape: attach both
        files and use the asset URLs in your index entry.
      </P>

      <H2 id="index-entry">Open a pull request</H2>
      <P>
        Add your entry to <Code>index.json</Code> in the{' '}
        <a href={INDEX_REPO} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          official index repository
        </a>
        :
      </P>
      <CodeBlock code={INDEX_ENTRY} />
      <P>
        Optional <Code>icon_url</Code>, <Code>banner_url</Code>, and <Code>readme_url</Code>{' '}
        fields drive the detail page in the app. The README renders as plain markdown (raw HTML
        shows as text), so lead with what the plugin does and a screenshot.
      </P>

      <H2 id="review">What review checks</H2>
      <Ul>
        <li>The manifest inside the artifact matches the index entry exactly (id, version, tier).</li>
        <li>The declared tier matches what the plugin actually does, including its networking.</li>
        <li>The signature verifies against the public key in the entry.</li>
        <li>
          The id is not squatting another author's namespace; <Code>app.streamnook.*</Code> is
          reserved for first-party plugins.
        </li>
      </Ul>

      <H2 id="updates">Updating a release</H2>
      <P>
        Upload a new signed zip and open a pull request that bumps <Code>version</Code>,{' '}
        <Code>artifact</Code>, and <Code>updated_at</Code> in your entry. Sign with the same key;
        the rotation flow is in{' '}
        <a href={SPEC.signing} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          SIGNING.md
        </a>
        .
      </P>

      <H2 id="policy">Index policy</H2>
      <Callout tone="warning">
        Plugins that run background automation or change how playback is resolved are outside the
        official index's policy and aren't accepted there. The plugin system supports third-party
        sources using the same signed index format, run by whoever chooses to operate them.
      </Callout>
      <P>
        The full walkthrough lives in{' '}
        <a href={PUBLISHING_URL} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          PUBLISHING.md
        </a>
        .
      </P>
    </>
  );
}
